import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { IconButton } from '../primitives/IconButton.js';
import { cx } from '../utils/cx.js';
import { useMediaQuery } from '../utils/useMediaQuery.js';
import styles from './ViewMenu.module.css';
import { Pressable } from '../primitives/Pressable.js';
import { ProximityScope } from '../primitives/ProximityHighlight.js';

export type ViewMenuItem<T extends string = string> = { value: T; label: string };

export type ViewMenuProps<T extends string> = {
  value: T;
  items: readonly ViewMenuItem<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
};

const OPEN_MS = 80;
const CLOSE_MS = 220;

export function ViewMenu<T extends string>({
  value,
  items,
  onChange,
  ariaLabel = 'Changer de vue',
}: ViewMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const hoverCapable = useMediaQuery('(hover: hover) and (pointer: fine)');
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
    openT.current = null;
    closeT.current = null;
  }, []);

  const scheduleOpen = useCallback(() => {
    if (!hoverCapable) return;
    clearTimers();
    openT.current = setTimeout(() => setOpen(true), OPEN_MS);
  }, [clearTimers, hoverCapable]);

  const scheduleClose = useCallback(() => {
    if (!hoverCapable) return;
    clearTimers();
    closeT.current = setTimeout(() => setOpen(false), CLOSE_MS);
  }, [clearTimers, hoverCapable]);

  const closeMenu = useCallback(() => {
    clearTimers();
    setOpen(false);
    setActiveIndex(-1);
    triggerRef.current?.querySelector('button')?.focus();
  }, [clearTimers]);

  const toggleMenu = useCallback(() => {
    clearTimers();
    setOpen((was) => !was);
  }, [clearTimers]);

  const selectItem = useCallback(
    (idx: number) => {
      const it = items[idx];
      if (!it) return;
      onChange(it.value);
      clearTimers();
      setOpen(false);
      setActiveIndex(-1);
      triggerRef.current?.querySelector('button')?.focus();
    },
    [clearTimers, items, onChange],
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [open]);

  useEffect(() => {
    if (activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const root = wrapRef.current;
      if (!root?.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    return () => document.removeEventListener('pointerdown', onDocPointer, true);
  }, [closeMenu, open]);

  const handleMenuKeyDown = (e: KeyboardEvent) => {
    if (items.length === 0) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i < 0 ? 0 : i + 1, items.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i < 0 ? items.length - 1 : i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectItem(activeIndex);
    }
  };

  return (
    <div
      ref={wrapRef}
      className={cx(styles.viewMenuWrap, open && styles.open)}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocusCapture={hoverCapable ? scheduleOpen : undefined}
      onBlurCapture={
        hoverCapable
          ? (e) => {
              const t = e.relatedTarget as Node | null;
              if (t && e.currentTarget.contains(t)) return;
              scheduleClose();
            }
          : undefined
      }
    >
      <div ref={triggerRef}>
      <IconButton
        type="button"
        variant="ghost"
        size="sm"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggleMenu}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (!open) {
              e.preventDefault();
              clearTimers();
              setOpen(true);
            }
          }
        }}
      >
        <IconChevronDown size={20} stroke={1.5} className={styles.chev} aria-hidden />
      </IconButton>
      </div>
      {open ? (
        <div className={styles.viewMenuPanel}>
          <ProximityScope
            axis="y"
            ref={menuRef}
            className={styles.viewMenu}
            role="menu"
            tabIndex={-1}
            onKeyDown={handleMenuKeyDown}
          >
            {items.map((it, idx) => (
              <Pressable
                key={it.value}
                ref={(el) => {
                  optionRefs.current[idx] = el;
                }}
                type="button"
                className={styles.viewMenuItem}
                role="menuitem"
                data-wp-proximity-item=""
                data-active={value === it.value ? 'true' : 'false'}
                data-keyboard-active={idx === activeIndex ? 'true' : 'false'}
                onClick={() => selectItem(idx)}
              >
                {it.label}
              </Pressable>
            ))}
          </ProximityScope>
        </div>
      ) : null}
    </div>
  );
}
